import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Cookies are HttpOnly so they survive page reloads. Some browser contexts
// (Safari ITP, third-party-cookie-blocking, embedded webviews) drop
// Set-Cookie, so we also stash the opaque session token in localStorage and
// attach it as an Authorization Bearer header. Backend accepts either.
api.interceptors.request.use((config) => {
  try {
    const tok = localStorage.getItem("lc_session_token");
    if (tok && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tok}`;
    }
  } catch { /* private mode / SSR */ }
  return config;
});

// ============================================================
//  ROOMS — 8 rooms, each mapped to one item.type.
//  `metaphor` picks the render component (VhsCase / BookSpine /
//  RecordSleeve / IndexCard / Polaroid / RecipeCard).
//  `tex` picks the hall-panel texture class.
//  Order here = display order on the hall.
// ============================================================
export const ROOMS = [
  { type: "movie",   name: "Movies",        metaphor: "vhs",     tex: "hall-tex-reel",   copy: "films kept",    one: "1 film kept" },
  { type: "tvshow",  name: "TV Series",     metaphor: "vhs",     tex: "hall-tex-tv",     copy: "series kept",   one: "1 series kept" },
  { type: "book",    name: "Books",         metaphor: "spine",   tex: "hall-tex-wood",   copy: "books kept",    one: "1 book kept" },
  { type: "music",   name: "Music",         metaphor: "record",  tex: "hall-tex-groove", copy: "records kept",  one: "1 record kept" },
  { type: "article", name: "Articles",      metaphor: "card",    tex: "hall-tex-lines",  copy: "articles kept", one: "1 article kept" },
  { type: "video",   name: "Videos",        metaphor: "vhs",     tex: "hall-tex-reel",   copy: "videos kept",   one: "1 video kept" },
  { type: "picture", name: "Photo Library", metaphor: "polaroid",tex: "hall-tex-dots",   copy: "photos kept",   one: "1 photo kept" },
  { type: "recipe",  name: "Recipes",       metaphor: "recipe",  tex: "hall-tex-cloth",  copy: "recipes kept",  one: "1 recipe kept" },
  // "commons" is a route, not a media type — it lives on its own page at
  // /commons. Skipping it from counts (see Hall) and Add/Room routing.
  { type: "commons", name: "The Commons",   metaphor: "commons", tex: "",                copy: "a quiet room", one: "a quiet room", to: "/commons", social: true },
];

export const ROOM_BY_TYPE = ROOMS.reduce((m, r) => (m[r.type] = r, m), {});

// Existing components (Add, ItemDetail, Settings, Collections) still read
// TYPE_META. Kept the shape but muted the colors for the dark theme.
export const TYPE_META = {
  movie:   { label: "Movies",   plural: "Movies",   accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "films to remember" },
  tvshow:  { label: "TV Shows", plural: "TV Shows", accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "series worth a season" },
  book:    { label: "Books",    plural: "Books",    accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "pages and chapters" },
  music:   { label: "Music",    plural: "Music",    accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "things to listen on" },
  article: { label: "Articles", plural: "Articles", accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "words to revisit" },
  video:   { label: "Videos",   plural: "Videos",   accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "short moving pictures" },
  picture: { label: "Photos",   plural: "Photos",   accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "images worth keeping" },
  recipe:  { label: "Recipes",  plural: "Recipes",  accent: "#B3895A", accent_soft: "#3a2f22", bg: "#241F1B", tape: "rgba(179,137,90,0.20)", border: "rgba(179,137,90,0.20)", motto: "things to cook one day" },
  unknown: { label: "Unsorted", plural: "Unsorted", accent: "#8E8371", accent_soft: "#2b2622", bg: "#241F1B", tape: "rgba(142,131,113,0.20)", border: "rgba(142,131,113,0.18)", motto: "awaiting a shelf" },
};

export const TYPE_ORDER = ROOMS.map(r => r.type);

// Types eligible for the daily pick (excluding pictures, per spec).
export const PICK_ELIGIBLE_TYPES = ["article", "music", "video", "movie", "tvshow", "recipe", "book"];
